import { useMemo, useState, type CSSProperties } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { DillTable, DillColumn } from '../../../../../general/Dill/DillTable';
import { openDillToast } from '../../../../../../utils/helpers';
import { useAppDispatch } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { updateSupplierBranches } from '../../../../../../redux/services/supplierAdditionalDetailsService';
import {
  updateUserSupplierBranches,
  updateUserSupplierDetails,
} from '../../../../../../redux/globalSlices/authSlice';
import {
  ErpDataLists,
  ErpSalesRep,
  mapSalesRepImportRow,
  mergeImportedSalesReps,
  SupplierBranch,
} from '@dill/dill-shared';

const UploadIcon = () => (
  <svg
    width={22}
    height={22}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 16V4M12 4l-4 4M12 4l4 4M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </svg>
);

const baseDropStyle: CSSProperties = {
  padding: '28px 16px',
  borderWidth: 2,
  borderRadius: 12,
  borderColor: '#e5e7eb',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border-color .2s ease-in-out, background-color .2s ease-in-out',
  textAlign: 'center',
};
const acceptDropStyle: CSSProperties = { borderColor: '#22c55e', backgroundColor: '#f0fdf4' };
const rejectDropStyle: CSSProperties = { borderColor: '#ef4444', backgroundColor: '#fef2f2' };

// One row as parsed from the file, plus what the merge preview decided about it.
interface PreviewRow {
  salesRepId: string;
  name: string;
  branchId: string;
  branchName: string;
  status: 'New' | 'Updated' | 'Skipped' | 'Error';
  reason?: string;
  branchMatch: 'matched' | 'unmatched' | 'none';
  isHouseRep: boolean;
}

const readFileAsBinaryString = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) resolve(event.target.result.toString());
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });

const parseFileToRawRows = async (file: File): Promise<Record<string, unknown>[]> => {
  const result = await readFileAsBinaryString(file);

  if (file.name.toLowerCase().endsWith('.csv')) {
    return new Promise((resolve, reject) => {
      Papa.parse(result, {
        header: true,
        skipEmptyLines: true,
        complete: (parsed) => resolve(parsed.data as Record<string, unknown>[]),
        error: (err: Error) => reject(err),
      });
    });
  }

  const workbook = XLSX.read(result, { type: 'binary' });
  const sheetName = workbook.SheetNames[0];
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
    raw: false,
    defval: '',
  });
};

// Bulk-imports an ERP sales rep roster (id/name/branch, no email) straight onto
// Supplier.erpDataLists.salesReps — same collection the "ERP Sales Rep" dropdown in the Add/Edit
// Sales Rep modal already reads from — AND, wherever a row's branch resolves to an existing
// branch, onto that branch's own salesRep list as an id-less entry: BranchSettings.tsx renders any
// salesRep entry with no `id` with a "No Dill Access" badge, since there's no Dill user (no email
// to invite) behind it. Deliberately doesn't touch Dill user accounts at all.
export const ImportSalesRepsModal = ({
  open,
  onClose,
  supplierId,
  existingErpDataLists,
  existingBranches,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  existingErpDataLists?: ErpDataLists | null;
  existingBranches: SupplierBranch[];
  onImported: (salesReps: ErpSalesRep[], branches: SupplierBranch[]) => void;
}) => {
  const existingSalesReps = existingErpDataLists?.salesReps ?? [];
  const dispatch = useAppDispatch();

  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  // The exact salesReps array to send if the user confirms - computed once at preview time so
  // the summary count and the actual write never disagree.
  const [pendingResult, setPendingResult] = useState<{
    salesReps: ErpSalesRep[];
    branches: SupplierBranch[];
    createdSalesReps: number;
    updatedSalesReps: number;
    unmatchedBranchCount: number;
  } | null>(null);

  const reset = () => {
    setFileName('');
    setParsing(false);
    setImporting(false);
    setParseError('');
    setPreviewRows([]);
    setPendingResult(null);
  };

  const handleCloseModal = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setParseError('');
    setFileName(file.name);
    try {
      const rawRows = await parseFileToRawRows(file);
      if (rawRows.length === 0) {
        setParseError('That file has no rows to import.');
        setParsing(false);
        return;
      }

      const validRows: Parameters<typeof mergeImportedSalesReps>[0]['rows'] = [];
      const errorRows: { row: Record<string, unknown>; error: string }[] = [];
      rawRows.forEach((rawRow) => {
        const mapped = mapSalesRepImportRow(rawRow);
        if ('error' in mapped) errorRows.push({ row: rawRow, error: mapped.error });
        else validRows.push(mapped);
      });

      const merged = mergeImportedSalesReps({
        existingSalesReps,
        existingBranches,
        rows: validRows,
      });

      const rowResultByRepId = new Map(merged.rowResults.map((r) => [r.salesRepId, r]));
      const statusByResultStatus: Record<'created' | 'updated' | 'skipped', PreviewRow['status']> =
        { created: 'New', updated: 'Updated', skipped: 'Skipped' };
      const rows: PreviewRow[] = validRows.map((row) => {
        const salesRepId = row.salesRepId.trim();
        const rowResult = rowResultByRepId.get(salesRepId);
        return {
          salesRepId,
          name: [row.firstName, row.middleInitial, row.lastName].filter(Boolean).join(' '),
          branchId: row.branchId,
          branchName: row.branchName,
          status: rowResult ? statusByResultStatus[rowResult.status] : 'New',
          reason: rowResult?.reason,
          branchMatch: rowResult?.branchMatch ?? 'none',
          isHouseRep: rowResult?.isHouseRep ?? false,
        };
      });
      errorRows.forEach(({ row, error }) => {
        rows.push({
          salesRepId: String(row['Salesrep ID'] ?? row.salesRepId ?? ''),
          name: [row['First Name'], row['Last Name']].filter(Boolean).join(' '),
          branchId: '',
          branchName: '',
          status: 'Error',
          reason: error,
          branchMatch: 'none',
          isHouseRep: false,
        });
      });

      setPreviewRows(rows);
      setPendingResult({
        salesReps: merged.salesReps,
        branches: merged.branches,
        createdSalesReps: merged.createdSalesReps,
        updatedSalesReps: merged.updatedSalesReps,
        unmatchedBranchCount: merged.unmatchedBranchCount,
      });
    } catch (err) {
      console.error(err);
      setParseError('Could not read that file. Make sure it is a .csv, .xls, or .xlsx export.');
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles[0]) handleFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
  });

  const dropStyle = useMemo(
    () => ({
      ...baseDropStyle,
      ...(isDragAccept ? acceptDropStyle : {}),
      ...(isDragReject ? rejectDropStyle : {}),
    }),
    [isDragAccept, isDragReject]
  );

  const columns: DillColumn<PreviewRow>[] = [
    { key: 'salesRepId', label: 'Sales Rep ID', width: 110 },
    { key: 'name', label: 'Name', width: 220 },
    {
      key: 'isHouseRep',
      label: 'House Rep',
      width: 100,
      render: (_value, row) =>
        row.isHouseRep ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200">
            House
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      key: 'branchName',
      label: 'Branch',
      width: 240,
      render: (_value, row) => {
        if (!row.branchName && !row.branchId) return <span className="text-gray-300">—</span>;
        return (
          <span className={row.branchMatch === 'unmatched' ? 'text-amber-600' : undefined}>
            {row.branchName || row.branchId}
            {row.branchMatch === 'unmatched' && (
              <span className="block text-[10px] text-amber-500">Branch not found</span>
            )}
          </span>
        );
      },
    },
    { key: 'branchId', label: 'Branch ID', width: 110 },
    {
      key: 'status',
      label: 'Status',
      width: 220,
      render: (_value, row) => {
        const cls =
          row.status === 'New'
            ? 'text-green-700 bg-green-50'
            : row.status === 'Updated'
            ? 'text-blue-700 bg-blue-50'
            : row.status === 'Skipped'
            ? 'text-amber-700 bg-amber-50'
            : 'text-red-700 bg-red-50';
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
            {row.status}
            {row.reason ? ` — ${row.reason}` : ''}
          </span>
        );
      },
    },
  ];

  const handleImport = async () => {
    if (!pendingResult) return;
    setImporting(true);
    // Send the full erpDataLists object (not just { salesReps }) so client redux state — a plain
    // shallow-merge, unlike the Firestore write below — doesn't drop the other lists
    // (territories/terms/branches) that live alongside salesReps in that same map.
    const updatedErpDataLists: ErpDataLists = {
      ...existingErpDataLists,
      salesReps: pendingResult.salesReps,
    };
    const result = await dispatch(
      updateSupplierBranches({
        supplierId,
        branches: pendingResult.branches,
      })
    );
    await dispatch(
      updateSupplier({
        supplierId,
        supplierDetails: { erpDataLists: updatedErpDataLists },
      })
    );
    setImporting(false);

    if (result.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Imported ${pendingResult.createdSalesReps} sales rep${
          pendingResult.createdSalesReps === 1 ? '' : 's'
        }${
          pendingResult.updatedSalesReps
            ? ` and updated ${pendingResult.updatedSalesReps} existing rep${
                pendingResult.updatedSalesReps === 1 ? '' : 's'
              }`
            : ''
        }${
          pendingResult.unmatchedBranchCount
            ? `. ${pendingResult.unmatchedBranchCount} rep${
                pendingResult.unmatchedBranchCount === 1 ? '' : 's'
              } couldn't be matched to a branch`
            : ''
        }.`,
      });
      dispatch(updateUserSupplierDetails({ erpDataLists: updatedErpDataLists }));
      dispatch(updateUserSupplierBranches(pendingResult.branches));
      onImported(pendingResult.salesReps, pendingResult.branches);
      handleCloseModal();
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to import sales reps.' });
    }
  };

  const skippedCount = previewRows.filter((r) => r.status === 'Skipped').length;
  const errorCount = previewRows.filter((r) => r.status === 'Error').length;

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Import Sales Members"
      subtitle="Upload a CSV or Excel export of sales reps — no email needed, these aren't turned into user accounts."
      maxWidth="900px"
      footer={
        pendingResult ? (
          <div className="flex items-center justify-between w-full">
            <DillButton onClick={handleCloseModal} label="Cancel" variant="secondary" />
            <DillButton
              onClick={handleImport}
              label={`Import ${
                pendingResult.createdSalesReps + pendingResult.updatedSalesReps
              } sales rep${
                pendingResult.createdSalesReps + pendingResult.updatedSalesReps === 1 ? '' : 's'
              }`}
              isLoading={importing}
              isDisabled={pendingResult.createdSalesReps + pendingResult.updatedSalesReps === 0}
            />
          </div>
        ) : (
          <div className="flex justify-end w-full">
            <DillButton onClick={handleCloseModal} label="Cancel" variant="secondary" />
          </div>
        )
      }>
      {!pendingResult ? (
        <div className="">
          <div {...getRootProps()} style={dropStyle} className="cursor-pointer">
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <UploadIcon />
              <p className="text-sm font-medium text-gray-700 m-0">
                {parsing
                  ? 'Reading file…'
                  : 'Drop a .csv, .xls, or .xlsx file here, or click to browse'}
              </p>
              <p className="text-xs text-gray-400 m-0">
                Required column: Sales Rep ID. Name and Branch columns are optional. A name
                containing &quot;house&quot; or &quot;hse&quot; is flagged as a House Rep — review
                that in the preview before importing.
              </p>
            </div>
          </div>
          {parseError && <p className="text-xs text-red-500 mt-3">{parseError}</p>}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 m-0">
              <span className="font-medium text-gray-700">{fileName}</span> — {previewRows.length}{' '}
              row
              {previewRows.length === 1 ? '' : 's'} found
            </p>
            <DillButton onClick={reset} label="Choose a different file" variant="ghost" />
          </div>
          <div className="flex gap-3">
            <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-xs text-green-700">
              <span className="font-bold">{pendingResult.createdSalesReps}</span> new sales rep
              {pendingResult.createdSalesReps === 1 ? '' : 's'}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
              <span className="font-bold">{pendingResult.updatedSalesReps}</span> updated
            </div>
            {pendingResult.unmatchedBranchCount > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700">
                <span className="font-bold">{pendingResult.unmatchedBranchCount}</span> branch
                {pendingResult.unmatchedBranchCount === 1 ? '' : 'es'} not found
              </div>
            )}
            {skippedCount > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700">
                <span className="font-bold">{skippedCount}</span> skipped (duplicate in file)
              </div>
            )}
            {errorCount > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700">
                <span className="font-bold">{errorCount}</span> row{errorCount === 1 ? '' : 's'}{' '}
                with errors
              </div>
            )}
          </div>
          <DillTable
            columns={columns}
            data={previewRows}
            rowKey={(row, index) => row.salesRepId || index}
            maxHeight="360px"
            striped
          />
        </div>
      )}
    </DillModal>
  );
};

export default ImportSalesRepsModal;
